import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { FiPhone } from "react-icons/fi";
import { toast } from "react-toastify";
import { useBrowserPhoneContext } from "./BrowserPhoneProvider";
import { BrowserPhoneState } from "./telephonyTypes";
import DialPad from "./DialPad";
import ActiveCallBar from "./ActiveCallBar";
import PhoneStatusBar from "./PhoneStatusBar";
import IncomingCallPopup from "./IncomingCallPopup";
import useCallCenterT from "../../../hooks/useCallCenterT";
import { useCallCenterCalls } from "../../../hooks/useCallCenterCalls";
import {
  canUseBrowserPhone,
  hasBrowserPhoneScreenAccess,
  isBrowserPhoneAvailable,
} from "./browserPhoneAccess";
import { isBrowserPhoneChannelEnabled, ORG_FEATURES_CHANGED_EVENT } from "../../../utils/orgFeatures";
import { getBrowserPhonePanelError, isBrowserPhoneUnavailable } from "./telephonyErrorMessages";
import { connectBrowserPhone, placeCall, runTelephonyAction } from "./placeCall";
import { getTelephonyTransportLabel } from "../../../utils/isDesktop";
import { getBrowserPhoneDisableMessage } from "./browserPhoneMessages";

const LIVE_CALL_STATES = new Set([
  BrowserPhoneState.INCOMING_CALL,
  BrowserPhoneState.INCOMING_CONNECTING,
  BrowserPhoneState.OUTGOING_CALL,
  BrowserPhoneState.ACTIVE_CALL,
  BrowserPhoneState.HOLD,
]);

const GlobalDialer = () => {
  const t = useCallCenterT();
  const location = useLocation();
  const navigate = useNavigate();
  const { store, actions } = useBrowserPhoneContext();
  const { createManual, loading: callLoading } = useCallCenterCalls();
  const [open, setOpen] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [orgFeaturesVersion, setOrgFeaturesVersion] = useState(0);
  void orgFeaturesVersion;

  const phoneConfig = store.config;
  const phoneAvailable = isBrowserPhoneAvailable(phoneConfig);
  const hasLiveCall = LIVE_CALL_STATES.has(store.state);
  const isDedicatedPhonePage = location.pathname === "/call-center/browser-phone";
  const browserPhoneEnabled = phoneAvailable;
  const providerName = phoneConfig?.providerName;

  useEffect(() => {
    if (!hasBrowserPhoneScreenAccess()) {
      return undefined;
    }
    actions.loadConfig().catch(() => undefined);
  }, [actions, orgFeaturesVersion]);

  useEffect(() => {
    const onOrgFeaturesChanged = () => {
      setOrgFeaturesVersion((value) => value + 1);
      if (!isBrowserPhoneChannelEnabled()) {
        setOpen(false);
        setKeypadOpen(false);
      }
    };
    window.addEventListener(ORG_FEATURES_CHANGED_EVENT, onOrgFeaturesChanged);
    return () => window.removeEventListener(ORG_FEATURES_CHANGED_EVENT, onOrgFeaturesChanged);
  }, []);

  const isIncoming =
    browserPhoneEnabled &&
    (store.state === BrowserPhoneState.INCOMING_CALL ||
      store.state === BrowserPhoneState.INCOMING_CONNECTING);
  const isCallActive =
    browserPhoneEnabled &&
    (store.state === BrowserPhoneState.OUTGOING_CALL ||
      store.state === BrowserPhoneState.ACTIVE_CALL ||
      store.state === BrowserPhoneState.HOLD);
  const dialerUiEnabled = canUseBrowserPhone();
  const showDialerPanel = dialerUiEnabled && !isDedicatedPhonePage && !isCallActive;
  const registrationFailed = browserPhoneEnabled && isBrowserPhoneUnavailable(store);
  const isConnecting = browserPhoneEnabled && store.state === BrowserPhoneState.CONNECTING;
  const phoneReady = !browserPhoneEnabled || store.state === BrowserPhoneState.REGISTERED;
  const showReset = browserPhoneEnabled && !isIncoming && !isCallActive;
  const panelError = getBrowserPhonePanelError(store, t, { providerName });
  const showIdleDialPad = !isCallActive && !isIncoming && !registrationFailed;
  const statusState = registrationFailed
    ? BrowserPhoneState.REGISTRATION_FAILED
    : browserPhoneEnabled
      ? store.state
      : BrowserPhoneState.REGISTRATION_FAILED;
  const extensionLabel =
    phoneConfig?.extension ||
    phoneConfig?.sipExtension ||
    phoneConfig?.agentExtension ||
    "EEMAD";

  useEffect(() => {
    if (isIncoming) {
      setOpen(true);
    }
    if (isCallActive) {
      setOpen(false);
    }
  }, [isCallActive, isIncoming]);

  useEffect(() => {
    if (!isCallActive) {
      setKeypadOpen(false);
    }
  }, [isCallActive]);

  const handleAcceptIncoming = useCallback(async () => {
    if (accepting) return;
    setAccepting(true);
    try {
      await runTelephonyAction({ action: actions.acceptCall, toast, t, providerName });
    } finally {
      setAccepting(false);
    }
  }, [accepting, actions, providerName, t]);

  const handleRejectIncoming = useCallback(async () => {
    if (accepting) return;
    await runTelephonyAction({ action: actions.rejectCall, toast, t, providerName });
  }, [accepting, actions, providerName, t]);

  useEffect(() => {
    if (
      open &&
      browserPhoneEnabled &&
      !isIncoming &&
      !isCallActive &&
      store.state !== BrowserPhoneState.REGISTRATION_FAILED &&
      store.state !== BrowserPhoneState.CONNECTING
    ) {
      connectBrowserPhone({ actions, toast, t, providerName });
    }
  }, [actions, browserPhoneEnabled, isCallActive, isIncoming, open, providerName, store.state, t]);

  const handleDial = useCallback(
    (phoneNumber) =>
      placeCall({
        browserPhoneEnabled,
        providerName,
        actions,
        createManual,
        phoneNumber,
        toast,
        t,
      }),
    [actions, browserPhoneEnabled, createManual, providerName, t]
  );

  const handleHangup = useCallback(
    () => runTelephonyAction({ action: actions.endCall, toast, t, providerName }),
    [actions, providerName, t]
  );

  const handleHold = useCallback(
    (hold) => runTelephonyAction({ action: () => actions.setHold(hold), toast, t, providerName }),
    [actions, providerName, t]
  );

  const handleOpenBrowserPhone = useCallback(() => {
    setOpen(false);
    navigate("/call-center/browser-phone");
  }, [navigate]);

  const handleResetPhone = useCallback(
    () => runTelephonyAction({ action: actions.resetCall, toast, t, providerName }),
    [actions, providerName, t]
  );

  if (!hasBrowserPhoneScreenAccess()) {
    return null;
  }

  if (!dialerUiEnabled && !hasLiveCall) {
    return null;
  }

  return (
    <>
      {isCallActive && !isDedicatedPhonePage && (
        <ActiveCallBar
          activeCall={store.activeCall}
          hold={store.hold}
          muted={store.muted}
          connecting={store.state === BrowserPhoneState.OUTGOING_CALL}
          onMuteChange={actions.setMute}
          onHoldChange={handleHold}
          onHangup={handleHangup}
          onToggleKeypad={() => setKeypadOpen((current) => !current)}
          onContacts={() => navigate("/call-center/tickets")}
          onNote={() => navigate("/call-center/tickets")}
          keypadOpen={keypadOpen}
          extensionLabel={extensionLabel}
          variant="dock"
        />
      )}

      {isCallActive && keypadOpen && !isDedicatedPhonePage && (
        <div className="fixed bottom-24 right-4 z-[9999] w-[min(100vw-2rem,17.5rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl">
          <DialPad
            onDial={() => undefined}
            disabled
            calling={false}
            callLabel={t("ccDialerCall")}
            callingLabel={t("ccDialerCall")}
            placeholder={t("ccDialerEnterNumber")}
          />
        </div>
      )}

      {open && showDialerPanel && (
        <div
          className="fixed bottom-24 right-4 z-[1200] flex w-[min(100vw-2rem,17.5rem)] max-h-[min(85vh,32rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
          role="dialog"
          aria-label={t("ccGlobalDialerTitle")}
        >
          <div className="flex shrink-0 items-center justify-between border-b border-slate-100 bg-[#2B7FFF] px-4 py-3 text-white">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <FiPhone className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
              {t("ccGlobalDialerTitle")}
            </div>
            <button
              type="button"
              className="rounded-lg p-1.5 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label={t("ccGlobalDialerMinimize")}
              disabled={isIncoming}
              onClick={() => setOpen(false)}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {registrationFailed ? (
              <div className="space-y-3">
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-center">
                  <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                    <ExclamationTriangleIcon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                    {t("ccPhoneStatusUnavailable")}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-amber-950">
                    {panelError || t("ccBrowserPhoneUnexpectedError")}
                  </p>
                </div>
                {showReset && (
                  <button
                    type="button"
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    onClick={handleResetPhone}
                  >
                    {t("ccGlobalDialerResetPhone")}
                  </button>
                )}
              </div>
            ) : (
              <>
                <PhoneStatusBar state={statusState} error={panelError} t={t} />
                {!browserPhoneEnabled && (
                  <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                    {getBrowserPhoneDisableMessage(phoneConfig, t)}
                  </p>
                )}
                {browserPhoneEnabled && !panelError && (
                  <p className="mt-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                    {getTelephonyTransportLabel(providerName)}
                  </p>
                )}

                {isIncoming && (
                  <div className="mt-3">
                    <IncomingCallPopup
                      visible
                      caller={
                        store.activeCall?.phoneNumber ||
                        store.activeCall?.remoteIdentity?.uri?.user ||
                        t("ccUnknownCaller")
                      }
                      accountNo={store.activeCall?.accountNo}
                      onAccept={handleAcceptIncoming}
                      onReject={handleRejectIncoming}
                      disabled={accepting || store.state === BrowserPhoneState.INCOMING_CONNECTING}
                      connecting={accepting || store.state === BrowserPhoneState.INCOMING_CONNECTING}
                      t={t}
                    />
                  </div>
                )}

                {showIdleDialPad && (
                  <div className="mt-3 space-y-2">
                    <DialPad
                      onDial={handleDial}
                      disabled={
                        !browserPhoneEnabled ||
                        isConnecting ||
                        callLoading ||
                        !phoneReady
                      }
                      calling={isConnecting || callLoading || (browserPhoneEnabled && !phoneReady)}
                      callLabel={t("ccDialerCall")}
                      callingLabel={
                        browserPhoneEnabled
                          ? phoneReady
                            ? t("ccDialerCall")
                            : t("ccGlobalDialerConnecting")
                          : t("ccInitiatingCall")
                      }
                      placeholder={t("ccDialerEnterNumber")}
                    />
                    {showReset && (
                      <button
                        type="button"
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center text-xs font-semibold text-slate-700 hover:bg-slate-100"
                        onClick={handleResetPhone}
                      >
                        {t("ccGlobalDialerResetPhone")}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="shrink-0 border-t border-slate-100 bg-slate-50/80 px-3 py-2.5">
            <button
              type="button"
              className="flex w-full items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-xs font-semibold text-[#2B7FFF] transition hover:bg-[#2B7FFF]/5"
              onClick={handleOpenBrowserPhone}
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
              {t("ccGlobalDialerOpenBrowserPhone")}
            </button>
          </div>
        </div>
      )}

      {dialerUiEnabled && !isCallActive && (
        <button
          type="button"
          className={`fixed bottom-6 right-4 z-[9998] flex h-14 w-14 items-center justify-center rounded-full text-white transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#2B7FFF] focus:ring-offset-2 ${
            isIncoming
              ? "animate-pulse bg-emerald-500 shadow-[0_8px_24px_rgba(16,185,129,0.4)]"
              : browserPhoneEnabled
                ? "bg-[#2B7FFF] shadow-[0_8px_24px_rgba(43,127,255,0.4)] hover:bg-[#1d6ef2]"
                : "bg-slate-500 shadow-[0_8px_24px_rgba(100,116,139,0.35)] hover:bg-slate-600"
          }`}
          aria-label={open ? t("ccGlobalDialerMinimize") : t("ccGlobalDialerOpen")}
          onClick={() => {
            if (isIncoming) {
              return;
            }
            setOpen((current) => !current);
          }}
        >
          <FiPhone className="h-6 w-6" strokeWidth={2.25} aria-hidden="true" />
          {isIncoming && (
            <span className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-rose-500" />
          )}
        </button>
      )}
    </>
  );
};

export default GlobalDialer;
